import { Lightning } from "@lightningjs/sdk";
import { ChannelListVertical } from "./components/ChannelListVertical/ChannelListVertical";
import { LiveVideoPlayer } from "./components/LiveVideoPlayer/LiveVideoPlayer";
import { ChannelItem } from "./components/ChannelItem/ChannelItem";
import { ChannelItemModel } from "../../lib/models";
import theme from "../../lib/theme";
import { MENU_WIDTH } from "../../lib/utils";
import { LiveChannelsBase } from "./LiveChannelsBase";

interface LiveChannelsVerticalTemplateSpec extends Lightning.Component.TemplateSpec {
    VideoPlayer: typeof LiveVideoPlayer;
    List: typeof ChannelListVertical;
    ChannelOverlay: typeof ChannelItem;
}

interface LiveChannelsTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

export class LiveChannelsVertical
    extends LiveChannelsBase
    implements Lightning.Component.ImplementTemplateSpec<LiveChannelsVerticalTemplateSpec>
{
    readonly VideoPlayer = this.getByRef("VideoPlayer")!;
    readonly List = this.getByRef("List")!;
    readonly ChannelOverlay = this.getByRef("ChannelOverlay")!;

    static override _template(): Lightning.Component.Template<LiveChannelsVerticalTemplateSpec> {
        return {
            w: theme.layout.w,
            h: theme.layout.h,
            VideoPlayer: {
                type: LiveVideoPlayer
            },
            List: {
                type: ChannelListVertical,
                x: MENU_WIDTH + 28,
                y: 40,
                w: theme.layout.w - MENU_WIDTH - 28,
                h: theme.layout.h - 40
            },
            ChannelOverlay: {
                type: ChannelItem,
                alpha: 0,
                x: theme.channelOverlay.content.x,
                y: theme.layout.h - theme.layout.gap.y - theme.channelOverlay.h
            }
        };
    }

    protected override get _videoPlayer(): LiveVideoPlayer {
        return this.VideoPlayer;
    }

    protected override get _channelList(): ChannelListVertical {
        return this.List;
    }

    protected override _showOverlay(channel: ChannelItemModel): void {
        this.ChannelOverlay.item = channel;
        this.ChannelOverlay.patch({ smooth: { alpha: 1 } });
    }

    protected override _hideOverlay(): void {
        this.ChannelOverlay.patch({ smooth: { alpha: 0 } });
    }

    // Left/right: switch channel when hidden. When list is showing, left/right bubble
    // up from the vertical list (it doesn't handle them), so guard is needed.
    override _handleLeft() {
        if (!this._showingList) {
            this._onChannelPrev();
            return true;
        }
        return false;
    }

    override _handleRight() {
        if (!this._showingList) {
            this._onChannelNext();
            return true;
        }
        return false;
    }

    // Up/down: show the list when hidden. When list is showing, the list handles
    // these itself and they don't bubble here.
    override _handleUp() {
        this._showList();
        return true;
    }

    override _handleDown() {
        this._showList();
        return true;
    }

    override _handleEnter() {
        if (this._currentChannel) {
            this._triggerOverlay(this._currentChannel);
        }
    }

    protected override _showList(): void {
        super._showList();
        this.application.emit("showMenu");
        this.List.patch({ smooth: { alpha: 1 } });
    }

    protected override _hideList(): void {
        super._hideList();
        this.List.patch({ smooth: { alpha: 0 } });
    }

    // Placeholder — vertical layout does not use channel preview
    protected override _onChannelPreviewPrev(): void {}
    protected override _onChannelPreviewNext(): void {}
}
